import { DashboardProject } from "@api/apiClient";
import i18n from "@i18n/i18n";
import { Box, Card, Checkbox, Typography, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import React from "react";

interface DashboardTileCardProps {
  project: DashboardProject;
  isSelected: boolean;
  onSelectionChange: (projectId: string, selected: boolean) => void;
  onNavigate: (projectId: string) => void;
  isSelectionDisabled?: boolean;
  enableSelection?: boolean;
}

const DashboardTileCard = observer((props: DashboardTileCardProps) => {
  const { project, isSelected, onSelectionChange, onNavigate, isSelectionDisabled = false, enableSelection = true } = props;
  const theme = useTheme();

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Stop propagation to prevent card click navigation
    event.stopPropagation();
    onSelectionChange(project.projectId!, event.target.checked);
  };

  const handleCheckboxClick = (event: React.MouseEvent) => {
    // Stop propagation to prevent card click navigation
    event.stopPropagation();
  };

  const handleCardClick = () => {
    onNavigate(project.projectId!);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Handle keyboard navigation for accessibility
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onNavigate(project.projectId!);
    }
  };

  return (
    <Card
      sx={{
        position: "relative",
        cursor: "pointer",
        width: "300px",
        transition: "all 0.2s ease-in-out",
        border: isSelected ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
        "&:hover": {
          boxShadow: theme.shadows[4],
          transform: "translateY(-2px)",
        },
        "&:focus-within": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
        },
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="button"
      aria-label={`${project.name} project card`}
    >
      {enableSelection && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 2,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            boxShadow: theme.shadows[2],
            transition: "background-color 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 1)",
            },
          }}
          onClick={handleCheckboxClick}
        >
          <Checkbox
            checked={isSelected}
            onChange={handleCheckboxChange}
            onClick={handleCheckboxClick}
            disabled={isSelectionDisabled}
            sx={{
              padding: 0,
              color: isSelectionDisabled ? theme.palette.action.disabled : theme.palette.primary.main,
              "&.Mui-checked": {
                color: theme.palette.primary.main,
              },
              "&.Mui-disabled": {
                color: theme.palette.action.disabled,
              },
            }}
            inputProps={{
              "aria-label": isSelectionDisabled 
                ? `Cannot select ${project.name} - maximum selection limit reached` 
                : `Select ${project.name} project`,
            }}
          />
        </Box>
      )}

      {/* Selected Overlay */}
      {isSelected && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.palette.primary.main,
            opacity: 0.08,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}

      {/* Card Content */}
      <Box onClick={handleCardClick}>
        {/* Project Image */}
        {project?.imageUrl && (
          <Box
            component="img"
            src={project?.imageUrl}
            width="100%"
            height="150px"
            alt={`${project.name} thumbnail`}
            sx={{ objectFit: "cover" }}
          />
        )}
        {!project?.imageUrl && (
          <Box
            component="img"
            src="/img/img-placeholder-1x.png"
            width="100%"
            height="150px"
            alt="Project placeholder"
            sx={{ objectFit: "contain" }}
          />
        )}

        {/* Project Details */}
        <Box p={1}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={project.name}
          >
            {project.name}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {i18n.t("dashboard.message.model-count", { count: Number(project?.modelCount) })}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
});

export default DashboardTileCard;
